import { createStyles } from 'antd-style'

import {
  FLOATING_CARD_SHADOW,
  PREVIEW_PANE_PADDING,
  SKELETON_ROW_CENTERED
} from '../styles'

import {
  ACTIVITY_PANEL_MIN_WIDTH,
  ACTIVITY_PANEL_WIDTH,
  COUNTER_SLOT_HEIGHT,
  GATED_OPACITY,
  RING_SIZE
} from './constants'

export const useStyles = createStyles(({ token, css }) => ({
  /**
   * The handoff cards are anchors wrapping a Card. The link must not repaint the
   * card's text or add an underline — it exists so the browser shows where the
   * card leads, nothing more.
   */
  cardLink: css`
    display: block;
    height: 100%;
    color: inherit;

    &:hover,
    &:focus-visible {
      color: inherit;
      text-decoration: none;
    }
  `,

  checkGlyph: css`
    flex: 0 0 auto;
    display: block;
    height: ${token.fontSize * token.lineHeight}px;
  `,

  phaseLabel: css`
    white-space: nowrap;
    font-size: ${token.fontSizeSM}px;
  `,

  stepCount: css`
    color: ${token.colorPrimary};
  `,

  /**
   * One pass of light across the primary action at the moment it unlocks. The
   * button clips it; the span is wider than it looks because the gradient fades
   * out well before its own edges.
   */
  actionSheen: css`
    position: absolute;
    inset-block: 0;
    inset-inline-start: 0;
    width: 26%;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.32),
      rgba(255, 255, 255, 0)
    );
  `,

  actionShell: css`
    position: relative;
    overflow: hidden;
    border-radius: ${token.borderRadius}px;
  `,

  activityPane: css`
    flex: 1 1 0;
    min-width: ${ACTIVITY_PANEL_MIN_WIDTH}px;
    /* Without this the pane keeps a flex item's default \`min-height: auto\` and
       refuses to shrink below its content, so the card's \`max-height: 100%\`
       resolved against a box that had already outgrown the slab — the cap never
       bit, nothing scrolled, and the slab's \`overflow: hidden\` cropped the
       panel's top and bottom off instead. */
    min-height: 0;
    display: flex;
    /* Centred on both axes: the panel sits *on* the picture at its own height
       rather than being stretched to the slab's, and scrolls inside once the
       content outgrows the space. */
    align-items: center;
    justify-content: center;
    padding: ${PREVIEW_PANE_PADDING}px;

    /* Transparent: the shell's dark slab showing through is a layer under the
       panel, not a reason for it to grow — the inset stays as it was. */
    background: transparent;

    ${SKELETON_ROW_CENTERED}
  `,

  activityShell: css`
    width: 100%;
    max-width: ${ACTIVITY_PANEL_WIDTH}px;
    max-height: 100%;
    min-height: 0;
    display: flex;
    overflow: hidden;
  `,

  activityCard: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: ${token.borderRadiusLG}px;
    background: ${token.colorBgContainer};

    &&& {
      border: none;
      box-shadow: ${FLOATING_CARD_SHADOW};
    }

    > .ant-card-body {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
    }
  `,

  /**
   * Held at the top while the blocks below it scroll: it names the workspace and
   * carries the build's verdict, which are the two things worth still having in
   * view once somebody has scrolled down the log. Opaque, or the rows travel
   * visibly underneath it.
   */
  activityHeader: css`
    position: sticky;
    top: 0;
    z-index: 1;
    flex: 0 0 auto;
    padding: ${token.paddingSM}px ${token.padding}px;
    border-block-end: 1px solid ${token.colorBorderSecondary};
    background: ${token.colorBgContainer};
  `,

  activityBlock: css`
    flex: 0 0 auto;
    min-height: 0;
    padding: ${token.padding}px ${token.padding}px 0;

    &:last-child {
      padding-block-end: ${token.padding}px;
    }
  `,

  dividedRow: css`
    padding-block: ${token.paddingSM}px;
    border-block-end: 1px solid ${token.colorBorderSecondary};
    overflow: hidden;

    &:last-of-type {
      border-block-end: none;
    }
  `,

  rowLabel: css`
    flex: 1;
    min-width: 0;
  `,

  glyph: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${token.fontSizeXL}px;

    .ant-badge-status-dot {
      display: block;
    }
  `,

  logGlyph: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${token.controlHeightSM}px;
    height: ${token.controlHeightSM}px;
    border-radius: ${token.borderRadiusSM}px;
    background: ${token.colorPrimaryBg};
    color: ${token.colorPrimary};
  `,

  facetIcon: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${token.controlHeightSM}px;
    height: ${token.controlHeightSM}px;
    border-radius: ${token.borderRadiusSM}px;
    background: ${token.colorFillQuaternary};
    color: ${token.colorTextSecondary};
  `,

  logGlyphMuted: css`
    background: ${token.colorFillQuaternary};
    color: ${token.colorTextQuaternary};
  `,

  mutedRow: css`
    color: ${token.colorTextTertiary};
  `,

  /**
   * Sits below the log rather than inside it: it is not something that happened,
   * it is the state the workspace was left in and the way out of it.
   */
  emptyCallout: css`
    flex: 0 0 auto;
    margin: 0 ${token.padding}px ${token.padding}px;
    padding: ${token.paddingSM}px ${token.padding}px;
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    background: ${token.colorFillQuaternary};
  `,

  emptyBody: css`
    font-size: ${token.fontSizeSM}px;
  `,

  /**
   * A measured value — the run's percentage. Mono, because the design system
   * puts numbers that change under the reader in mono so the digits do not
   * shift sideways as they count up.
   */
  stepStatus: css`
    flex: 0 0 auto;
    white-space: nowrap;
    font-family: ${token.fontFamilyCode};
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextQuaternary};
  `,

  /**
   * A word, not a value — "done", "running", "by your choice". Inter, because
   * mono is for ids, timestamps and keys; a status in mono reads as a code the
   * reader is supposed to recognise. Same size and colour as {@link stepStatus},
   * which keeps the mono for the run's percentage.
   */
  rowState: css`
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextQuaternary};
  `,

  /**
   * The sentence above the build bar. It shares one row with the percentage and
   * the details button and must never take a second one, so it yields instead:
   * `flex`/`min-width` give AntD's `ellipsis` something to truncate against,
   * and the running label's trailing parenthetical is what goes first.
   */
  setupLabel: css`
    flex: 1;
    min-width: 0;
    font-size: ${token.fontSizeSM}px;
  `,

  /** The percentage + details cluster keeps its width; the label yields. */
  setupMeta: css`
    flex: 0 0 auto;
  `,

  domainLine: css`
    font-family: ${token.fontFamilyCode};
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextQuaternary};
  `,

  counterWindow: css`
    height: ${COUNTER_SLOT_HEIGHT}px;
    overflow: hidden;
  `,

  counterSlot: css`
    height: ${COUNTER_SLOT_HEIGHT}px;
    font-size: ${token.fontSizeHeading3}px;
    line-height: ${COUNTER_SLOT_HEIGHT}px;
    font-variant-numeric: tabular-nums;
  `,

  counterSlotArrived: css`
    color: ${token.colorPrimary};
  `,

  ring: css`
    flex: 0 0 auto;
    position: relative;
    width: ${RING_SIZE}px;
    height: ${RING_SIZE}px;
  `,

  ringSvg: css`
    position: absolute;
    inset: 0;
  `,

  ringAvatar: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,

  ringCheck: css`
    position: absolute;
    inset-block-end: 0;
    inset-inline-end: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${token.controlHeightXS}px;
    height: ${token.controlHeightXS}px;
    border-radius: ${token.borderRadiusLG * 2}px;
    background: ${token.colorPrimary};
    color: ${token.colorBgContainer};
  `,

  gated: css`
    opacity: ${GATED_OPACITY};
    pointer-events: none;
    transition: opacity ${token.motionDurationSlow} ${token.motionEaseInOut};
  `,

  ungated: css`
    transition: opacity ${token.motionDurationSlow} ${token.motionEaseInOut};
  `
}))
